import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { extractErrorMessage } from '../../utils/errorHandler';
import axios from 'axios';
import API_CONFIG from '../../config/apiConfig';

const ForgotPasswordPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (event?: React.FormEvent | React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const authServiceUrl = API_CONFIG.AUTH_SERVICE_URL || API_CONFIG.BASE_URL;
      const resetUrl = `${authServiceUrl}/api/v1/auth/password/reset/request`;

      await axios.post(resetUrl, {
        email: email,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout
      });

      setSuccess(true);
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container component="main" maxWidth="xs">
        <div
          style={{
            marginTop: 64,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            style={{
              padding: 32,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              backgroundColor: '#fff',
            }}
          >
            <CheckCircleIcon
              style={{
                fontSize: 60,
                color: '#2e7d32',
                marginBottom: 16,
              }}
            />

            <Typography
              component="h1"
              variant="h5"
              style={{
                marginBottom: 16,
                fontWeight: 'bold',
              }}
            >
              Check Your Email
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              style={{ marginBottom: 24 }}
            >
              If an account exists for <strong>{email}</strong>, you will receive a password reset link shortly.
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              style={{ marginBottom: 24 }}
            >
              Please check your email and follow the instructions to reset your password.
            </Typography>

            <div style={{ textAlign: 'center', width: '100%' }}>
              <Link
                component={RouterLink}
                to="/login"
                variant="body1"
                style={{ fontWeight: 500 }}
              >
                Return to Login
              </Link>
            </div>
          </Paper>
        </div>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <div
        style={{
          marginTop: 64,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          style={{
            padding: 32,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            backgroundColor: '#fff',
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            style={{
              marginBottom: 8,
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #325BA8 30%, #4DC4D2 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Prismatic Forge
          </Typography>

          <Typography variant="h6" style={{ marginBottom: 8 }}>
            Reset Your Password
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            style={{ marginBottom: 24 }}
          >
            Enter your email address and we'll send you a link to reset your password.
          </Typography>

          {error && (
            <Alert severity="error" style={{ width: '100%', marginBottom: 16 }}>
              {error}
            </Alert>
          )}

          <form noValidate style={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              style={{ marginTop: 24, marginBottom: 16, paddingTop: 12, paddingBottom: 12 }}
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Send Reset Link'
              )}
            </Button>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to="/login"
                variant="body2"
                color="text.secondary"
              >
                Back to Login
              </Link>
            </div>
          </form>
        </Paper>
      </div>
    </Container>
  );
};

export default ForgotPasswordPage;
